import { Images } from "../../utils/imageConstants";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { scrollToTop } from "../../hooks/useScrollToTop";
import type { IGetAllLeagues, ILeagueDetails, ILeaguesResponse } from "./types/header";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { QUERY_KEY } from "../../services/queryEndPoint";
import { Api } from "../../services/interceptor";
import useDebounce from "../../hooks/useDebounce";
import {
  extractGlobalSearchItems,
  mergeWebSearchLeagueIntoList,
  type GlobalSearchItem,
} from "../../utils/websearchParse";
import SearchBar from "../common/SearchBar";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { setActiveTab, type TabKey } from "../../redux/slice/activeTabSlice";
import { selectSelectedLeague, setSelectedLeague } from "../../redux/slice/selectedLeagueSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuthToken, resolvePublicImageUrl } from "../../helper/helper";
import GlobalSearchBar from "../common/GlobalSearchBar";



const SubHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = !!getAuthToken();
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [showLeagues, setShowLeagues] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const toggleBtnRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const isTogglingRef = useRef<boolean>(false);
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector((s) => s?.activeTab?.value);
  const selectedLeague = useAppSelector(selectSelectedLeague);

  /** Join/umpire/scorer approvals — same capability as editing league settings. */
  const showRequestsTab = isAuthenticated && Boolean(selectedLeague?.canEditLeague);
  const shareUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const handleShareLeague = async () => {
    const url = shareUrl;
    if (!url) return;

    const shareData = {
      title: selectedLeague?.leagueName || "League",
      text: `Check out ${selectedLeague?.leagueName || "this league"} on CricCenter`,
      url,
    };

    try {
      if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
        await navigator.share(shareData);
        return;
      }
    } catch {
      // If user cancels native share, do not show an error toast.
      return;
    }

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard.");
        return;
      }
    } catch {
      // fall through to manual-copy message
    }

    toast("Copy this link: " + url, { icon: "🔗" });
  };

  const handleTabClick = (tab: NonNullable<TabKey>) => {
    dispatch(setActiveTab(tab));
    sessionStorage.setItem("activeTab", tab);
    setDetailsOpen(false);
    const leagueId = selectedLeague?.leagueId;
    navigate(leagueId ? `/my-leagues/${leagueId}` : "/leagues-home");
    // Scroll to top when switching tabs
    scrollToTop();
  };

  const { data: leagues = [] } = useQuery<IGetAllLeagues[]>({
    queryKey: [QUERY_KEY.getAllLeagues, isAuthenticated ? "auth" : "guest"],
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnReconnect: true,
    queryFn: async () => {
      const response = await Api.get<ILeaguesResponse>(QUERY_KEY.getAllLeagues);
      const payload = response?.data as unknown;
      const asRecord = (value: unknown): Record<string, unknown> | null =>
        typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
      const normalizeLeagues = (items: unknown[]): IGetAllLeagues[] =>
        items
          .map((item) => {
            const rec = asRecord(item);
            if (!rec) return null;
            const rawLeagueId = rec.leagueId ?? rec.id ?? rec.leagueID ?? rec.league_id;
            const rawLeagueName = rec.leagueName ?? rec.name ?? rec.league_name;
            if (!rawLeagueId) return null;
            return {
              ...rec,
              leagueId: String(rawLeagueId),
              leagueName: String(rawLeagueName ?? ""),
            } as unknown as IGetAllLeagues;
          })
          .filter((league): league is IGetAllLeagues => !!league);
      const payloadRecord = asRecord(payload);
      const nestedData = asRecord(payloadRecord?.data);

      if (Array.isArray(payload)) return normalizeLeagues(payload);
      if (Array.isArray(payloadRecord?.leagues)) return normalizeLeagues(payloadRecord.leagues);
      if (Array.isArray(payloadRecord?.leagueResponseList)) return normalizeLeagues(payloadRecord.leagueResponseList);
      if (Array.isArray(nestedData?.leagueResponseList)) return normalizeLeagues(nestedData.leagueResponseList);
      if (Array.isArray(nestedData?.leagues)) return normalizeLeagues(nestedData.leagues);
      if (Array.isArray(payloadRecord?.data)) return normalizeLeagues(payloadRecord.data);
      return [];
    },
  });

  useEffect(() => {
    if (!leagues?.length) return;
    const selectedLeagueId = selectedLeague?.leagueId;
    // Only default to the first league when nothing is selected yet.
    // If we reset whenever the id is missing from getAllLeagues, we wipe choices from
    // global search (or deep links): that API often returns leagues not listed in the header dropdown.
    if (!selectedLeagueId) {
      dispatch(setSelectedLeague(leagues[0]));
    }
  }, [leagues, selectedLeague, dispatch]);

  // Tabs that exist within the league shell (rendered by TabContentWrapper).
  // Used to decide whether to preserve a user-clicked tab when navigating from a
  // tournament back to the league shell (Bug B: tab routing tournament ↔ league).
  const LEAGUE_SHELL_TABS: TabKey[] = [
    "leagues-home",
    "tournaments",
    "requests",
    "about",
    "gallery",
    "grounds",
    "players",
    "waiver",
  ];

  // Handle browser back navigation and direct URL navigation by setting appropriate active tab based on current route
  useEffect(() => {
    const pathname = location.pathname;

    // For detail pages, always set the parent tab (even on refresh)
    // This ensures the correct tab is active when navigating from detail pages

    // Ground details page - set to "grounds" tab
    if (pathname.includes("/ground-details/")) {
      const tab = "grounds";
      dispatch(setActiveTab(tab));
      sessionStorage.setItem("activeTab", tab);
    }
    // Tournament details pages - set to "tournaments" tab
    else if (
      pathname.includes("/tournamentDetails/") ||
      pathname.includes("/tournaments/tournaments-details") ||
      /\/my-leagues\/[^/]+\/tournaments\//.test(pathname)
    ) {
      const tab = "tournaments";
      dispatch(setActiveTab(tab));
      sessionStorage.setItem("activeTab", tab);
    }
    // Create tournament - set to "tournaments" tab
    else if (pathname.includes("/create-tournament")) {
      const tab = "tournaments";
      dispatch(setActiveTab(tab));
      sessionStorage.setItem("activeTab", tab);
    }
    // Create/edit waiver - set to "waiver" tab
    else if (pathname.includes("/create-waiver")) {
      const tab = "waiver";
      dispatch(setActiveTab(tab));
      sessionStorage.setItem("activeTab", tab);
    }
    // Team info - set to "teams" tab
    else if (pathname.includes("/tournaments/team-info")) {
      const tab = "teams";
      dispatch(setActiveTab(tab));
      sessionStorage.setItem("activeTab", tab);
    }
    // Player profile - set to "teams" tab
    else if (pathname.includes("/tournaments/player-profile")) {
      const tab = "teams";
      dispatch(setActiveTab(tab));
      sessionStorage.setItem("activeTab", tab);
    }
    // Matches details - set to "tournaments" tab
    else if (pathname.includes("/matches-details")) {
      const tab = "tournaments";
      dispatch(setActiveTab(tab));
      sessionStorage.setItem("activeTab", tab);
    }
    // League shell route. Preserve the active tab if it's already a valid
    // league-shell tab (e.g. user clicked About from inside a tournament — keep
    // them on About instead of forcing Leagues Home). Otherwise reset to default.
    else if (/^\/my-leagues\/[^/]+\/?$/.test(pathname)) {
      const current = activeTab ?? sessionStorage.getItem("activeTab");
      const preserve = current && LEAGUE_SHELL_TABS.includes(current as TabKey);
      const nextTab: TabKey = preserve ? (current as TabKey) : "leagues-home";
      dispatch(setActiveTab(nextTab));
      sessionStorage.setItem("activeTab", nextTab ?? "leagues-home");
    }
    // For absolute home routes, keep Home selected.
    else if (
      pathname === "/leagues-home" ||
      pathname === "/"
    ) {
      dispatch(setActiveTab("home"));
      sessionStorage.setItem("activeTab", "home");
    }
  }, [location.pathname, dispatch]);

  const debouncedLeagueSearch = useDebounce(search, 350);
  const isTypingBeforeDebounce =
    search.trim().length > 0 && search.trim() !== debouncedLeagueSearch.trim();

  const { data: webLeagueHits = [], isError } = useQuery<GlobalSearchItem[]>({
    queryKey: [QUERY_KEY.websearch, "subheader-league-dropdown", debouncedLeagueSearch],
    enabled: showLeagues && debouncedLeagueSearch.trim().length > 0,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await Api.get(QUERY_KEY.websearch, {
        params: { name: debouncedLeagueSearch.trim() },
      });
      return extractGlobalSearchItems(response?.data).filter((i) => i.type === "league");
    },
  });

  const searchQuery = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!search.trim()) {
      return leagues.filter((l) => (l.leagueName ?? "").toLowerCase().includes(searchQuery));
    }
    if (isTypingBeforeDebounce) {
      return [];
    }
    if (isError) {
      return [];
    }
    return webLeagueHits.map((item) => mergeWebSearchLeagueIntoList(item, leagues));
  }, [
    search,
    searchQuery,
    leagues,
    webLeagueHits,
    isTypingBeforeDebounce,
    isError,
  ]);

  const many = filtered.length > 7;

  const { data: leagueDetails } = useQuery<ILeagueDetails>({
    queryKey: [QUERY_KEY.getLeagueDetails, selectedLeague?.leagueId],
    queryFn: async () => {
      const url = `${QUERY_KEY.getLeagueDetails}/${selectedLeague?.leagueId}`;
      const response = await Api.get(url);
      return response?.data;
    },
    enabled: !!selectedLeague?.leagueId,
  });

  // Robust outside-click + Escape close
  useEffect(() => {
    if (!showLeagues) return;

    const handleOutside = (e: Event) => {
      // Don't close if we're in the middle of toggling
      if (isTogglingRef.current) return;

      const target = e.target as Node | null;
      const path = (e as Event & { composedPath?: () => Node[] }).composedPath?.();

      const inPopover =
        !!popoverRef.current &&
        ((path && path.includes(popoverRef.current)) ||
          (target && popoverRef.current.contains(target)));

      const inToggle =
        !!toggleBtnRef.current &&
        ((path && path.includes(toggleBtnRef.current)) ||
          (target && toggleBtnRef.current.contains(target)));

      if (!inPopover && !inToggle) {
        setShowLeagues(false);
        setSearch("");
      }
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowLeagues(false);
        setSearch("");
      }
    };

    // Use click event (not mousedown) to ensure React's onClick fires first
    // Add a small delay to allow state updates to complete
    const handleClick = (e: Event) => {
      // Use requestAnimationFrame to ensure this runs after React's event handlers
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          handleOutside(e);
        });
      });
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [showLeagues]);

  useEffect(() => {
    if (!detailsOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDetailsOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detailsOpen]);

  useEffect(() => {
    if (!detailsOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [detailsOpen]);

  return (
    <>
      <div className={`${detailsOpen ? "sub-header" : "sub-header sub-header-not-open"}`}>
        <div className="header-container custom-container">
          <div className="header-content">
            {/* Top row: [back | home] cluster, league name, round (mobile).
                Desktop: display:contents so row children join header-content flex; leading is one tight group. */}
            <div className="sub-header-top-row">
              <div className="sub-header-leading">
                <button
                  type="button"
                  className="sub-header-back"
                  aria-label="Go back"
                  onClick={() => {
                    if (location.pathname.includes("/tournaments/team-info")) {
                      const tab = "teams";
                      dispatch(setActiveTab(tab));
                      sessionStorage.setItem("activeTab", tab);
                      const leagueId = selectedLeague?.leagueId;
                      navigate(leagueId ? `/my-leagues/${leagueId}` : "/leagues-home");
                      return;
                    }

                    navigate(-1);
                  }}
                >
                  <img src={Images.back_arrow} alt="" aria-hidden />
                </button>

                {/* <span className="sub-header-vrule sub-header-vrule--after-back" aria-hidden="true" /> */}

                <button
                  type="button"
                  className="sub-header-home"
                  aria-label="Home"
                  onClick={() => {
                    dispatch(setActiveTab("home"));
                    sessionStorage.setItem("activeTab", "home");
                    navigate("/");
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path
                      fill="currentColor"
                      d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8h5z"
                    />
                  </svg>
                </button>

                <span className="sub-header-vrule sub-header-vrule--before-title" aria-hidden="true" />
              </div>

              <button
                ref={toggleBtnRef}
                className="heading"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  isTogglingRef.current = true;
                  if (showLeagues) {
                    setShowLeagues(false);
                    setSearch("");
                  } else {
                    setShowLeagues(true);
                  }
                  setDetailsOpen(false);
                  setTimeout(() => {
                    isTogglingRef.current = false;
                  }, 0);
                }}
                aria-haspopup="dialog"
                aria-expanded={showLeagues}
              >
                <span className="heading-label">{selectedLeague?.leagueName ?? "-"}</span>
                {showLeagues ? (
                  <img src={Images.league_dropup_icon} alt="" aria-hidden />
                ) : (
                  <img src={Images.subheader_dropdown} alt="" aria-hidden />
                )}
              </button>
            </div>

            <div className="sub-header-nav-row">
              <div className="divider" aria-hidden="true" />
              <nav className="tabs" aria-label="League sections">

                <button
                  type="button"
                  className={`tab-link ${activeTab === "leagues-home" ? "active" : "inactive-tab"}`}
                  onClick={() => handleTabClick("leagues-home")}
                >
                  League Home
                </button>
                <button
                  type="button"
                  className={`tab-link ${activeTab === "tournaments" ? "active" : "inactive-tab"}`}
                  onClick={() => handleTabClick("tournaments")}
                >
                  Tournaments
                </button>
                {showRequestsTab && (
                  <button
                    type="button"
                    className={`tab-link ${activeTab === "requests" ? "active" : "inactive-tab"}`}
                    onClick={() => handleTabClick("requests")}
                  >
                    Request
                  </button>
                )}
                <button
                  type="button"
                  className={`tab-link ${activeTab === "about" ? "active" : "inactive-tab"}`}
                  onClick={() => handleTabClick("about")}
                >
                  About
                </button>
                <button
                  type="button"
                  className={`tab-link ${activeTab === "gallery" ? "active" : "inactive-tab"}`}
                  onClick={() => handleTabClick("gallery")}
                >
                  Gallery
                </button>
                <button
                  type="button"
                  className={`tab-link ${activeTab === "grounds" ? "active" : "inactive-tab"}`}
                  onClick={() => handleTabClick("grounds")}
                >
                  Grounds
                </button>
                <button
                  type="button"
                  className={`tab-link ${activeTab === "waiver" ? "active" : "inactive-tab"}`}
                  onClick={() => handleTabClick("waiver")}
                >
                  Waiver
                </button>
                <button
                  type="button"
                  className={`tab-link ${activeTab === "players" ? "active" : "inactive-tab"}`}
                  onClick={() => handleTabClick("players")}
                >
                  Players
                </button>
              </nav>
            </div>

            <button type="button" className="sub-header-share" onClick={() => void handleShareLeague()}>
              <span className="share-icon">
                <img src={Images.share_icon} alt="" aria-hidden />
              </span>
              Share
            </button>

            {/* Desktop-only global search — hidden on mobile */}
            <GlobalSearchBar />

            {/* League dropdown popover — position:absolute relative to header-content */}
            {showLeagues && (
              <SearchBar
                popoverRef={popoverRef}
                many={many}
                search={search}
                setSearch={setSearch}
                showState={showLeagues}
                leagues={filtered}
                selectedLeagueId={selectedLeague?.leagueId ?? null}
                onSelect={(league) => {
                  dispatch(setSelectedLeague(league));
                  setShowLeagues(false);
                  setSearch("");
                  navigate(`/my-leagues/${league.leagueId}`);
                }}
              />
            )}
          </div>
        </div >
      </div >

      <div
        id="subheader-details"
        className={`details ${detailsOpen ? "open" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setDetailsOpen(false);
        }}
      >
        <div className="details-inner custom-container">
          <div className="details-league-card">
            <div className="details-league-card__main">
              <div className="logo-wrap">
                <img
                  src={
                    resolvePublicImageUrl(leagueDetails?.imageUri) ||
                    resolvePublicImageUrl(selectedLeague?.imageUri) ||
                    Images.match_card_image
                  }
                  alt=""
                />
              </div>
              <div className="details-league-card__content">
                <div className="title-line">
                  <h2>{leagueDetails?.leagueName ?? selectedLeague?.leagueName ?? "-"}</h2>
                  {/* <button type="button" className="share" onClick={() => void handleShareLeague()}>
                    <span className="share-icon">
                      <img src={Images.share_icon} alt="" aria-hidden />
                    </span>
                    Share
                  </button> */}
                </div>
                <div className="details-meta-grid">
                  <div className="details-meta-item">
                    <span className="label">League category</span>
                    <span className="value">
                      {leagueDetails?.roleName ?? selectedLeague?.roleName ?? "—"}
                    </span>
                  </div>
                  <div className="details-meta-item">
                    <span className="label">Association</span>
                    <span className="value">
                      {leagueDetails?.association ?? selectedLeague?.association ?? "—"}
                    </span>
                  </div>
                  <div className="details-meta-item">
                    <span className="label">Location</span>
                    <span className="value">
                      {leagueDetails?.country ?? selectedLeague?.country ?? "—"}
                    </span>
                  </div>
                  <div className="details-meta-item">
                    <span className="label">State</span>
                    <span className="value">
                      {leagueDetails?.state ?? selectedLeague?.state ?? "—"}
                    </span>
                  </div>
                  <div className="details-meta-item">
                    <span className="label">City</span>
                    <span className="value">
                      {leagueDetails?.city ?? selectedLeague?.city ?? "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="organizer">
              <div className="org-card">
                <img
                  className="avatar"
                  src={
                    resolvePublicImageUrl(leagueDetails?.organizer?.imageUri) ||
                    Images.card_info_image
                  }
                  alt=""
                />
                <div className="org-info">
                  <div className="name">
                    {[
                      leagueDetails?.organizer?.playerFirstName,
                      leagueDetails?.organizer?.playerLastName,
                    ]
                      .filter(Boolean)
                      .join(" ")
                      .trim() ||
                      leagueDetails?.organizerName ||
                      selectedLeague?.organizerName ||
                      "—"}
                  </div>
                  <div className="role">Organizer</div>
                </div>
                <span className="chev">›</span>
              </div>
            </div>
          </div>
        </div >
      </div >
      {/* </div> */}
    </>
  );
};

export default SubHeader;
